import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { PolicyEditor } from '../components/PolicyEditor';
import { RaciAssignments } from '../components/RaciAssignments';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { NimbusTable } from '../components/NimbusTable';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { documentApi, documentRegistersApi, peopleApi, departmentsApi, parseApiError } from '../services/api';
import {
  DPIA_RISK_DIMENSIONS, RISK_SCORES, RISK_SCORE_LABELS,
  RESIDUAL_RISK_LABELS, RESIDUAL_RISK_TONE,
} from '../constants/privacy';
import type { RiskScore, RiskDimensionEntry, ResidualRiskLevel } from '../constants/privacy';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import Select from 'react-select';
import { reactSelectStyles } from '../utils/reactSelectStyles';

type Doc = {
  documentId?: string;
  title?: string;
  documentType?: string;
  domainArea?: string;
  author?: string;
  departmentId?: string;
  confidentialityLevel?: string;
  status?: 'draft' | 'published' | 'archived' | string;
  content?: string;
  registerId?: string;
  entityId?: string;
  entityType?: string;
  publishedAt?: string;
  versionNumber?: number;
  majorVersion?: number;
  minorVersion?: number;
  reviewPeriod?: string;
  nextReviewDate?: string;
  updatedAt?: number;
  contentRev?: number;
  docNumber?: string;
  // AI-drafted content proposed since this document's last publish - written by the approval-
  // time drafting flow's append_drafted_row step, never merged into `content` until a human
  // explicitly accepts (optionally editing the HTML first) or rejects each one via
  // acceptPendingChange/rejectPendingChange. publish_document refuses to publish while any
  // entry here still has status 'pending', and clears this list once publish succeeds - each
  // entry's outcome survives on that publish's own version snapshot for the Change Log.
  pendingChanges?: PendingChange[];
  // Part 4's structured risk dimensions (documentType 'DPIA' only) and the rollup computed
  // from them - see constants/privacy.ts for why this is a separate field from dpiaVerdict.
  riskDimensions?: RiskDimensionEntry[];
  residualRiskLevel?: ResidualRiskLevel;
};

type PendingChange = {
  id: string;
  useCaseId: string;
  proposedHtml: string;
  finalHtml?: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt?: number;
  resolvedAt?: number;
  resolvedBy?: string;
  // Which existing section this is actually about (e.g. 'processingActivity', 'subprocessor') -
  // set server-side at drafting time. Absent on anything drafted before this field existed.
  // Mirrored against SECTION_TARGET_PATTERNS below purely to show the inline preview in the
  // right place; the real, authoritative placement decision is made server-side, by the exact
  // same matching logic, when the change is actually accepted (prod-nimbus-documents'
  // _find_section_insertion_point) - this only has to be right often enough to be a useful
  // preview, not perfectly in sync forever.
  targetSection?: string;
};

type Template = {
  title?: string;
  type?: string;
  domain?: string;
  content?: string;
};

// A document section parsed from the AI HTML (each <h2> becomes one section)
type DocSection = {
  id: string;          // data-step-id or generated key
  heading: string;     // text of the section's own top-level heading
  html: string;        // inner HTML of this section (excluding the heading itself)
};

// Sections split on <h2> by default (the common case: a single <h1> title followed by
// flat <h2> "chapters", no deeper numbering). But a document that numbers its own top-level
// parts as "1.", "2.", "3." - and its subsections as "1.1", "1.1.1" - authors those top-level
// parts as further <h1>s (see the heading-level convention: N. -> h1, N.N -> h2, N.N.N -> h3).
// For that document, splitting on h2 would wrongly promote "3.1 Scope Statement" to its own
// section while "3. Scope of the ISMS" - the real top-level part - gets silently swallowed into
// whichever h2 section preceded it. So: if the body contains any h1 beyond the leading title
// one, that document uses h1 as its section level, and every h2/h3 below it is nested content
// within its parent section, not a section of its own.
type SectionSplitTag = 'h1' | 'h2';

// Parse document HTML into sections. Every document is section-based - there is no separate
// single-editor mode (see the loadedDoc effect below for why: having both meant the app would
// silently jump from one to the other mid-edit whenever typing a heading happened to cross the
// "2 or more h2s" threshold this function used to require). A document with one heading at the
// split level, or none at all, still becomes exactly one section, so there's always something
// to show and always exactly one code path to maintain.
//
// The leading <h1> is dropped (it duplicates the separate title field this page already has and
// would otherwise show the title twice), but everything else before the first split-level
// heading - a real, previously-authored paragraph - becomes that first section's own content
// instead of being silently discarded, which the original version did whenever section mode
// applied at all.
function parseHtmlIntoSections(html: string): { sections: DocSection[]; splitTag: SectionSplitTag } {
  if (!html || html.startsWith('{')) {
    return { sections: [{ id: 'section-0', heading: '', html: '<p></p>' }], splitTag: 'h2' }; // delta JSON — nothing to parse, start fresh
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  if (div.firstElementChild?.tagName === 'H1') {
    div.removeChild(div.firstElementChild);
  }

  const splitTag: SectionSplitTag = div.querySelector('h1') ? 'h1' : 'h2';
  const headings = Array.from(div.querySelectorAll(splitTag));

  if (headings.length === 0) {
    return { sections: [{ id: 'section-0', heading: '', html: div.innerHTML.trim() || '<p></p>' }], splitTag };
  }

  const sections: DocSection[] = [];

  const preambleNodes: Node[] = [];
  for (let node = div.firstChild; node && node !== headings[0]; node = node.nextSibling) {
    preambleNodes.push(node);
  }
  if (preambleNodes.some((n) => (n.textContent || '').trim())) {
    const wrapper = document.createElement('div');
    preambleNodes.forEach((n) => wrapper.appendChild(n.cloneNode(true)));
    sections.push({ id: 'section-preamble', heading: '', html: wrapper.innerHTML });
  }

  const splitTagUpper = splitTag.toUpperCase();
  headings.forEach((heading, idx) => {
    const stepId = heading.getAttribute('data-step-id') || `section-${idx}`;
    const headingText = heading.textContent || '';
    // Collect all sibling nodes until the next section-level heading
    const contentNodes: Node[] = [];
    let next: Element | null = heading.nextElementSibling;
    while (next && next.tagName !== splitTagUpper) {
      contentNodes.push(next as Node);
      next = next.nextElementSibling;
    }
    const wrapper = document.createElement('div');
    contentNodes.forEach((n) => wrapper.appendChild((n as Element).cloneNode(true)));
    sections.push({ id: stepId, heading: headingText, html: wrapper.innerHTML });
  });
  return { sections, splitTag };
}

// Reassemble sections back into a single HTML string for storage. A section with no heading
// (the "content before the first real heading" case parseHtmlIntoSections produces) is emitted
// with no heading wrapper at all, so a round trip through parse -> save -> parse lands back in
// the same shape rather than accumulating empty heading tags. `splitTag` must match whatever
// parseHtmlIntoSections used to split this document, so a round trip doesn't flatten a
// document's real h1-numbered sections down to h2 (or vice versa).
function assembleSectionsToHtml(title: string, sections: DocSection[], splitTag: SectionSplitTag): string {
  const parts = sections.map((s) =>
    s.heading.trim() ? `<${splitTag} data-step-id="${s.id}">${s.heading}</${splitTag}>\n${s.html}` : s.html
  );
  return `<h1>${title}</h1>\n${parts.join('\n')}`;
}

// Mirrors prod-nimbus-documents/lambda_function.py's own _SECTION_TARGET_PATTERNS /
// _TRAILING_SECTION_PATTERN exactly (kept in sync by hand, same convention already used
// elsewhere in this codebase for backend/frontend pairs that must agree on a vocabulary) - used
// ONLY to decide where the inline preview below renders a still-pending change. The real,
// authoritative placement decision is made server-side when the change is actually accepted;
// this only has to be right often enough to be a useful preview.
const SECTION_TARGET_PATTERNS: Record<string, RegExp> = {
  subprocessor: /\bdata processors\b|\bsub-?processors\b/i,
  processingActivity: /\btypes of processing\b|\bprocessing activit/i,
};
const TRAILING_SECTION_PATTERN = /\bversion history\b|\bchange log\b|\brevision history\b/i;

// A document last edited through the old ReactQuill single-editor may still have a Quill
// Delta JSON blob stored as its content (that editor serialised via editor.getContents() on
// every save, regardless of how the document was loaded). PolicyEditor only ever accepts
// plain HTML, so feeding it a Delta string directly would render as unparsed literal text.
// There is no Quill instance left in this file to convert it, so the safe move is to open
// such a document with an empty editor and say so, rather than corrupt or silently drop it.
function isLegacyDelta(raw: string): boolean {
  return raw.trim().startsWith('{');
}

// The API client throws `API Error: <status> - <json body>` (see services/api.ts). Parsed
// here so a 409 - the server refusing a save because someone else changed this document since
// this page last saw it - gets distinct handling (stop autosave, explain what happened) rather
// than the generic "failed to save" toast, using the server's own message rather than a second,
// possibly-drifting copy of it.

// One row of Part 4's risk assessment - its own component (not inline JSX in a .map) because
// each row owns its own draft edit state; hooks can't be called conditionally/in a loop inside
// the parent's own render body.
const DpiaRiskDimensionRow: React.FC<{
  dimension: string;
  saved?: RiskDimensionEntry;
  onSave: (riskScore: RiskScore, mitigation: string, residualRiskAcceptable: boolean) => void;
  saving: boolean;
  readOnly: boolean;
}> = ({ dimension, saved, onSave, saving, readOnly }) => {
  const [riskScore, setRiskScore] = useState<RiskScore>(saved?.riskScore || 'low');
  const [mitigation, setMitigation] = useState(saved?.mitigation || '');
  const [residualRiskAcceptable, setResidualRiskAcceptable] = useState(!!saved?.residualRiskAcceptable);
  const dirty = !saved
    || riskScore !== saved.riskScore
    || mitigation !== saved.mitigation
    || residualRiskAcceptable !== !!saved.residualRiskAcceptable;

  return (
    <tr>
      <td style={{ minWidth: '12rem' }}>{dimension}</td>
      <td style={{ width: '140px' }}>
        <select className="np-select" value={riskScore} disabled={readOnly} onChange={(e) => setRiskScore(e.target.value as RiskScore)}>
          {RISK_SCORES.map((s) => <option key={s} value={s}>{RISK_SCORE_LABELS[s]}</option>)}
        </select>
      </td>
      <td style={{ minWidth: '260px' }}>
        <textarea
          className="np-textarea"
          rows={2}
          style={{ width: '100%' }}
          value={mitigation}
          disabled={readOnly}
          placeholder="What reduces this risk, if anything?"
          onChange={(e) => setMitigation(e.target.value)}
        />
      </td>
      <td style={{ width: '110px' }}>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            checked={residualRiskAcceptable}
            disabled={readOnly}
            onChange={(e) => setResidualRiskAcceptable(e.target.checked)}
          />
        </div>
      </td>
      <td style={{ width: '90px' }}>
        {!readOnly && (
          <button
            type="button"
            className="np-btn np-btn-ghost np-btn-sm"
            disabled={saving || !dirty}
            onClick={() => onSave(riskScore, mitigation, residualRiskAcceptable)}
          >
            {saved ? 'Update' : 'Save'}
          </button>
        )}
      </td>
    </tr>
  );
};

export const DocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const swal = useSwal();

  const registerIdFromUrl = searchParams.get('registerId') || '';
  const templateId = searchParams.get('template') || '';

  const queryClient = useQueryClient();

  const [docMeta, setDocMeta] = useState<Doc>({});
  const [lastSavedTime, setLastSavedTime] = useState<string>('');
  const [exportingPdf, setExportingPdf] = useState<boolean>(false);
  const [hasLegacyDeltaContent, setHasLegacyDeltaContent] = useState<boolean>(false);
  const [showPublishModal, setShowPublishModal] = useState<boolean>(false);
  const [changeReasonInput, setChangeReasonInput] = useState<string>('');
  // A document with no register (e.g. a per-use-case DPIA, which is deliberately entity-linked
  // rather than register-parented) used to hit a dead-end error on publish with no way to
  // recover short of leaving the page. Picking or creating one right here, instead, is what
  // publishMutation's own registerId fallback below reads.
  const [pendingRegisterId, setPendingRegisterId] = useState<string>('');
  const [showNewRegisterField, setShowNewRegisterField] = useState<boolean>(false);
  const [newRegisterName, setNewRegisterName] = useState<string>('');
  const [showChangeLogModal, setShowChangeLogModal] = useState<boolean>(false);
  // The only editing surface this page has - see parseHtmlIntoSections for why there's no
  // separate single-editor mode any more. null only very briefly, before the loadedDoc effect
  // (or the new-document effect) has run once.
  const [sections, setSections] = useState<DocSection[] | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'editor' | 'meta'>('editor');
  // (Re)initialising `sections` from server content must happen exactly once per document,
  // not every time loadedDoc's reference changes - see the loadedDoc effect below.
  const initializedDocIdRef = useRef<string | undefined>(undefined);
  // Which heading level parseHtmlIntoSections split this document's sections on, so
  // assembleSectionsToHtml reassembles at the same level instead of flattening a document's
  // real h1-numbered top-level sections down to h2.
  const sectionSplitTagRef = useRef<SectionSplitTag>('h2');
  // The contentRev this page last saw for the loaded document - sent back on every save as
  // expectedRev so the server can reject a save whose starting point is stale (someone else
  // changed the document since this page last synced with it) instead of silently overwriting
  // their change. A monotonic counter rather than a timestamp, since two saves landing in the
  // same wall-clock second would otherwise be indistinguishable. Advanced on load and after
  // every successful save. Defaults to 0 (rather than undefined) to match the server's own
  // if-missing-treat-as-0 handling for documents saved before this field existed.
  const lastKnownContentRevRef = useRef<number>(0);
  // True for the duration of ANY in-flight content save (draft save, publish's own pre-publish
  // save, or a force-save) - checked by the automatic triggers (the 60s autosave interval, the
  // register-change debounce) before they fire, so an autosave tick can never race a save that's
  // already in progress and read the same not-yet-advanced expectedRev, which would 409 against
  // its own sibling request rather than an actual external change.
  const isSavingRef = useRef(false);
  // Set when a save/publish is rejected as a conflict (409) - rendered as a persistent banner
  // rather than relying on the save toast alone, since that toast disappears after a few
  // seconds while autosave stays silently disabled until the user acts.
  const [conflictState, setConflictState] = useState<{ message: string } | null>(null);

  const { data: registers = [] } = useQuery<any[]>({
    queryKey: ['documentRegisters'],
    queryFn: documentApi.getRegisters,
  });

  const { data: people = [] } = useQuery<any[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: departments = [] } = useQuery<{ departmentId: string; name: string }[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const isPublished = (docMeta.status || 'draft') === 'published';
  const pendingChanges = docMeta.pendingChanges || [];
  const unresolvedChanges = pendingChanges.filter((c) => c.status === 'pending');
  const acceptedChanges = pendingChanges.filter((c) => c.status === 'accepted');

  // Where each unresolved change's inline preview actually renders - mirrors the server's own
  // insertion logic (prod-nimbus-documents' _find_section_insertion_point /
  // _find_before_trailing_section) against this page's already-parsed `sections`, so the
  // preview shows roughly where accepting it will really land instead of always the tail end.
  // Keyed by section index: changes in the map render immediately after that section; anything
  // left in `trailing` renders after the section right before a Version History/Change Log
  // section if one exists, or after the very last section otherwise - same two-tier fallback
  // the backend applies.
  const pendingBySectionIdx = useMemo(() => {
    const map = new Map<number, PendingChange[]>();
    if (!sections || sections.length === 0) return map;
    const trailing: PendingChange[] = [];
    unresolvedChanges.forEach((c) => {
      const pattern = c.targetSection ? SECTION_TARGET_PATTERNS[c.targetSection] : undefined;
      const matchIdx = pattern ? sections.findIndex((s) => pattern.test(s.heading)) : -1;
      if (matchIdx >= 0) {
        map.set(matchIdx, [...(map.get(matchIdx) || []), c]);
      } else {
        trailing.push(c);
      }
    });
    if (trailing.length > 0) {
      const versionHistoryIdx = sections.findIndex((s) => TRAILING_SECTION_PATTERN.test(s.heading));
      const anchorIdx = versionHistoryIdx > 0 ? versionHistoryIdx - 1 : sections.length - 1;
      map.set(anchorIdx, [...(map.get(anchorIdx) || []), ...trailing]);
    }
    return map;
  }, [sections, unresolvedChanges]);

  const autoSaveRef = useRef<number | null>(null);

  const { data: loadedDoc, isLoading } = useQuery<Doc>({
    queryKey: ['document', id],
    queryFn: async () => {
      const data = await documentApi.getOne(id!);
      return (data as any).document || data;
    },
    enabled: !!id,
  });

  // Determine the original document ID for version fetching
  // If viewing a snapshot (DOC-xxx-v0.1), extract the original ID (DOC-xxx)
  // Or use the originalDocumentId field if present
  const originalDocId = useMemo(() => {
    if (!docMeta.documentId) return null;
    
    // If document has originalDocumentId field, it's a snapshot
    if ((docMeta as any).originalDocumentId) {
      return (docMeta as any).originalDocumentId;
    }
    
    // If documentId contains "-v", it's a snapshot ID - extract original
    const match = docMeta.documentId.match(/^(DOC-[a-f0-9-]+)-v\d+\.\d+$/);
    if (match) {
      return match[1];
    }
    
    // Otherwise it's the original document
    return docMeta.documentId;
  }, [docMeta.documentId, (docMeta as any).originalDocumentId]);

  const { data: versionsData } = useQuery<any>({
    queryKey: ['documentVersions', originalDocId],
    queryFn: () => documentApi.getVersions(originalDocId!),
    enabled: !!originalDocId,
  });

  const versions = useMemo(() => {
    const v = versionsData?.versions || [];
    return Array.isArray(v) ? v : [];
  }, [versionsData]);

  const isLatestVersion = useMemo(() => {
    if (versions.length === 0) return true; // No versions yet, so this is latest
    const latestVersionId = versions[0]?.documentId || versions[0]?.id;
    return !latestVersionId || id === latestVersionId;
  }, [versions, id]);

  useEffect(() => {
    if (!loadedDoc) return;

    const next: Doc = {
      ...loadedDoc,
      documentId: loadedDoc.documentId,
      // Only ever backfills registerId from entityId when this document is actually
      // register-parented (entityType 'register', or unset on an older document that
      // predates this field). For any other entityType (e.g. 'ai_use_case' on an
      // AI-drafted DPIA - DocumentManager.tsx:20's real union), entityId means something
      // else entirely - which entity this document is ABOUT, not which register it's filed
      // under - and copying it into registerId here corrupted the real link the next time
      // this document saved (saveDraftMutation below used to force entityType back to
      // 'register' on every save, regardless of what it actually was).
      registerId: (!loadedDoc.entityType || loadedDoc.entityType === 'register')
        ? (loadedDoc.registerId || loadedDoc.entityId)
        : loadedDoc.registerId,
    };

    setDocMeta(next);

    // Everything below (re)initialises `sections` from the server's copy of the content, which
    // must only ever happen once per document, not every time loadedDoc's reference changes.
    // saveDraftMutation's onSuccess writes its response straight into this query's cache via
    // setQueryData (so a save doesn't need a network refetch) - which still changes loadedDoc's
    // identity and re-runs this effect. Re-parsing then would silently blow away whatever the
    // user was doing in the meantime: editing a section heading while an autosave lands would
    // reset that heading mid-keystroke, and - since parseHtmlIntoSections used to require 2+
    // headings to treat a document as section-based at all - typing a second heading into what
    // was then a free-form editor would cross that threshold on the very next autosave and the
    // whole page would silently jump from one editing surface to a completely different one,
    // mid-edit, with no action taken by the user beyond typing a heading.
    if (initializedDocIdRef.current === loadedDoc.documentId) return;
    initializedDocIdRef.current = loadedDoc.documentId;

    const raw = loadedDoc.content || '';
    const legacy = isLegacyDelta(raw);
    setHasLegacyDeltaContent(legacy);
    const { sections: parsed, splitTag } = parseHtmlIntoSections(raw);
    sectionSplitTagRef.current = splitTag;
    lastKnownContentRevRef.current = loadedDoc.contentRev ?? 0;
    setSections(parsed);
  }, [loadedDoc]);

  // Initialize for new documents
  useEffect(() => {
    if (id) return;

    // If navigated here with an AI draft, inject it
    const aiDraft = (location.state as any)?.aiDraft;

    setDocMeta((prev) => ({
      ...prev,
      status: 'draft',
      registerId: prev.registerId || registerIdFromUrl || aiDraft?.registerId || undefined,
      documentType: aiDraft?.documentType || prev.documentType,
    }));

    if (aiDraft?.html) {
      const { sections: parsed, splitTag } = parseHtmlIntoSections(aiDraft.html);
      sectionSplitTagRef.current = splitTag;
      setSections(parsed);
    } else {
      sectionSplitTagRef.current = 'h2';
      setSections([{ id: 'section-0', heading: '', html: '<p></p>' }]);
    }

    // Load template if requested (skip if AI draft already provided content)
    const loadTemplate = async () => {
      if (!templateId || aiDraft?.html) return;
      try {
        const resp = await fetch(`${import.meta.env.BASE_URL}templates/${encodeURIComponent(templateId)}.json`);
        if (!resp.ok) return;
        const tpl = (await resp.json()) as Template;

        setDocMeta((prev) => ({
          ...prev,
          title: tpl.title || prev.title,
          documentType: tpl.type || prev.documentType,
          domainArea: tpl.domain || prev.domainArea,
        }));

        if (tpl.content) {
          const { sections: parsed, splitTag } = parseHtmlIntoSections(tpl.content);
          sectionSplitTagRef.current = splitTag;
          setSections(parsed);
        }
      } catch {
        // ignore
      }
    };

    void loadTemplate();
  }, [id, registerIdFromUrl, templateId, user, location.state]);

  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      const contentToSave = assembleSectionsToHtml(docMeta.title || '', sections || [], sectionSplitTagRef.current);

      const registerId = docMeta.registerId || registerIdFromUrl || undefined;

      const payload: any = {
        title: docMeta.title || '',
        documentType: docMeta.documentType || '',
        domainArea: docMeta.domainArea || '',
        author: docMeta.author || '',
        departmentId: docMeta.departmentId || '',
        confidentialityLevel: docMeta.confidentialityLevel || '',
        status: 'draft',
        content: contentToSave,
        reviewPeriod: docMeta.reviewPeriod || '',
        nextReviewDate: docMeta.nextReviewDate || '',
        // registerId/entityType/entityId only included when this document is genuinely
        // register-parented - confirmed live as a real corruption bug: this used to send
        // entityType: 'register' unconditionally on every save (create AND update), which
        // silently overwrote a non-register-linked document's real entityType/entityId (e.g.
        // an AI-drafted DPIA's entityType: 'ai_use_case' link to its source use case) the very
        // first time someone opened and saved it from this page.
        ...(registerId ? { registerId, entityType: 'register', entityId: registerId } : {}),
      };

      if (docMeta.documentId) {
        // Updating an existing doc — registerId is already stored server-side, no need to re-send.
        // expectedRev lets the server reject this save if the document changed since we last
        // synced with it, instead of silently overwriting that change.
        return documentApi.update(docMeta.documentId, { ...payload, expectedRev: lastKnownContentRevRef.current });
      }

      // Creating a new document — registerId is required
      if (!registerId) {
        throw new Error('Missing registerId. Open from Documents list or pass ?registerId=...');
      }
      return documentApi.create(payload);
    },
    onMutate: () => {
      isSavingRef.current = true;
    },
    onSettled: () => {
      isSavingRef.current = false;
    },
    onSuccess: (saved: any) => {
      const normalized = (saved as any).document || saved;
      const newId = normalized.documentId || normalized.id;

      // Update docMeta directly from the save response so fields like
      // reviewPeriod/nextReviewDate are preserved without waiting for a refetch
      setDocMeta((prev) => ({
        ...prev,
        ...normalized,
        documentId: newId || prev.documentId,
        registerId: normalized.registerId || normalized.entityId || prev.registerId,
      }));

      if (typeof normalized.contentRev === 'number') {
        lastKnownContentRevRef.current = normalized.contentRev;
      }
      setConflictState(null);

      if (newId && newId !== id) {
        // New document — navigate to its permanent URL
        navigate(`/documents/${encodeURIComponent(newId)}`, { replace: true });
      }

      const now = new Date().toLocaleTimeString();
      setLastSavedTime(now);

      // Always refresh the documents list
      queryClient.invalidateQueries({ queryKey: ['documents'] });

      if (newId && newId !== id) {
        // New document — invalidate to load fresh data at the new URL
        queryClient.invalidateQueries({ queryKey: ['document', newId] });
        queryClient.invalidateQueries({ queryKey: ['documentVersions', newId] });
      } else if (id) {
        // For updates, write the ALL_NEW response directly into the cache so that
        // when the user navigates away and returns, the loadedDoc useEffect gets
        // fresh data (with reviewPeriod, nextReviewDate etc.) instead of the stale
        // pre-save cache entry. This avoids a network refetch while keeping the
        // cache consistent.
        queryClient.setQueryData(['document', id], normalized);
      }
    },
    onError: (e: Error) => {
      const { status, message } = parseApiError(e);
      if (status === 409) {
        // The stale token that just got rejected will just get rejected again on the next
        // 60s tick - stop hammering the user with the same failure until they reload or
        // force-save. A toast alone isn't enough here (it disappears in a few seconds while
        // autosave stays silently off), so this also drives a persistent banner.
        if (autoSaveRef.current) {
          window.clearInterval(autoSaveRef.current);
          autoSaveRef.current = null;
        }
        setConflictState({ message });
        toast.error(message);
        return;
      }
      toast.error(`Failed to save draft: ${message}`);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async ({ versionType, changeReason }: { versionType: 'major' | 'minor'; changeReason: string }) => {
      if (!docMeta.documentId) throw new Error('Save the draft first');
      const registerId = docMeta.registerId || registerIdFromUrl || pendingRegisterId || undefined;
      if (!registerId) throw new Error('This document is not linked to a register. Pick or create one above before publishing.');

      // First, save any unsaved changes
      const contentToSave = assembleSectionsToHtml(docMeta.title || '', sections || [], sectionSplitTagRef.current);

      const payload: any = {
        title: docMeta.title || '',
        documentType: docMeta.documentType || '',
        domainArea: docMeta.domainArea || '',
        author: docMeta.author || '',
        departmentId: docMeta.departmentId || '',
        confidentialityLevel: docMeta.confidentialityLevel || '',
        status: 'draft',
        content: contentToSave,
        reviewPeriod: docMeta.reviewPeriod || '',
        nextReviewDate: docMeta.nextReviewDate || '',
        // Only sent when this document didn't already have one - update_document maps this to
        // entityType/entityId server-side, and omitting it entirely leaves an existing link
        // untouched (a whitelist partial update), same convention saveDraftMutation follows.
        ...(!docMeta.registerId && !registerIdFromUrl ? { registerId } : {}),
      };

      // This pre-publish save must advance lastKnownContentRevRef itself, exactly like
      // saveDraftMutation's onSuccess does - otherwise a successful save here followed by an
      // unrelated failure in the publish() call below would leave the ref stale, and the very
      // next autosave tick would 409 against the rev this save itself just advanced, accusing
      // an uninvolved "someone else" of a change the user made themselves.
      const updated = await documentApi.update(docMeta.documentId, { ...payload, expectedRev: lastKnownContentRevRef.current });
      const normalizedUpdate = (updated as any).document || updated;
      if (typeof normalizedUpdate.contentRev === 'number') {
        lastKnownContentRevRef.current = normalizedUpdate.contentRev;
      }

      // Then publish the version
      return documentApi.publish(docMeta.documentId, versionType, changeReason);
    },
    onMutate: () => {
      isSavingRef.current = true;
    },
    onSettled: () => {
      isSavingRef.current = false;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documentVersions', docMeta.documentId] });
      setShowPublishModal(false);
      setChangeReasonInput('');
      setConflictState(null);
      toast.success('Document published.');
      window.location.reload();
    },
    onError: (e: Error) => {
      const { status, message } = parseApiError(e);
      if (status === 409) {
        if (autoSaveRef.current) {
          window.clearInterval(autoSaveRef.current);
          autoSaveRef.current = null;
        }
        setConflictState({ message });
        toast.error(message);
        return;
      }
      toast.error(`Failed to publish: ${message}`);
    },
  });

  const createRegisterMutation = useMutation({
    mutationFn: (name: string) => documentRegistersApi.create({ name }),
    onSuccess: (created: any) => {
      const newId = created?.registerId || created?.register?.registerId || created?.item?.registerId;
      if (!newId) {
        toast.error('Register created but no registerId was returned.');
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['documentRegisters'] });
      setPendingRegisterId(newId);
      setShowNewRegisterField(false);
      setNewRegisterName('');
      toast.success('Register created.');
    },
    onError: (e: Error) => toast.error(`Could not create the register: ${parseApiError(e).message}`),
  });

  // Accepting merges finalHtml into `content` server-side; a full reload (not just a query
  // invalidation) is required afterward - same reasoning publishMutation's own onSuccess
  // already applies here: this page's `sections` state is reconstructed from `content` once on
  // load and never re-synced from the server otherwise, so the very next autosave tick would
  // silently overwrite the server's newly-accepted content with this page's now-stale local
  // copy if we didn't force a fresh read.
  const [resolvingChangeId, setResolvingChangeId] = useState<string | null>(null);
  const [editHtmlDraft, setEditHtmlDraft] = useState<string>('');
  const acceptChangeMutation = useMutation({
    mutationFn: ({ changeId, editedHtml }: { changeId: string; editedHtml?: string }) =>
      documentApi.acceptPendingChange(docMeta.documentId!, changeId, editedHtml),
    onSuccess: () => {
      toast.success('Change accepted and added to the draft.');
      window.location.reload();
    },
    onError: (e: Error) => toast.error(`Couldn't accept this change: ${parseApiError(e).message}`),
  });
  const rejectChangeMutation = useMutation({
    mutationFn: (changeId: string) => documentApi.rejectPendingChange(docMeta.documentId!, changeId),
    onSuccess: () => {
      toast.success('Change rejected.');
      window.location.reload();
    },
    onError: (e: Error) => toast.error(`Couldn't reject this change: ${parseApiError(e).message}`),
  });

  // Unlike accept/reject above, this never touches `content`/`sections` - a plain query-cache
  // + local docMeta merge is safe here, no full reload needed.
  const upsertRiskDimensionMutation = useMutation({
    mutationFn: ({ dimension, riskScore, mitigation, residualRiskAcceptable }: {
      dimension: string; riskScore: RiskScore; mitigation: string; residualRiskAcceptable: boolean;
    }) => documentApi.upsertRiskDimension(docMeta.documentId!, dimension, { riskScore, mitigation, residualRiskAcceptable }),
    onSuccess: (result: any) => {
      const updated = result?.document || result;
      setDocMeta((prev) => ({ ...prev, riskDimensions: updated.riskDimensions, residualRiskLevel: updated.residualRiskLevel }));
      toast.success('Saved.');
    },
    onError: (e: Error) => toast.error(`Couldn't save this dimension: ${parseApiError(e).message}`),
  });

  const forceSaveMutation = useMutation({
    mutationFn: async () => {
      if (!docMeta.documentId) throw new Error('No document to save');

      // The whole point of a force-save is that our own lastKnownContentRevRef is known to be
      // stale - re-fetch the document's CURRENT rev right before saving, rather than reusing
      // anything cached, so this intentionally overwrites exactly the version we just saw and
      // decided to override, not a third, even-newer one that might have landed since.
      const fresh = await documentApi.getOne(docMeta.documentId);
      const freshDoc = (fresh as any).document || fresh;

      const contentToSave = assembleSectionsToHtml(docMeta.title || '', sections || [], sectionSplitTagRef.current);
      const registerId = docMeta.registerId || registerIdFromUrl || undefined;
      const payload: any = {
        title: docMeta.title || '',
        documentType: docMeta.documentType || '',
        domainArea: docMeta.domainArea || '',
        author: docMeta.author || '',
        departmentId: docMeta.departmentId || '',
        confidentialityLevel: docMeta.confidentialityLevel || '',
        status: 'draft',
        content: contentToSave,
        reviewPeriod: docMeta.reviewPeriod || '',
        nextReviewDate: docMeta.nextReviewDate || '',
        expectedRev: freshDoc.contentRev ?? 0,
        // Same fix as saveDraftMutation above - never force entityType back to 'register' for
        // a document that isn't actually register-parented.
        ...(registerId ? { registerId, entityType: 'register', entityId: registerId } : {}),
      };
      return documentApi.update(docMeta.documentId, payload);
    },
    onMutate: () => {
      isSavingRef.current = true;
    },
    onSettled: () => {
      isSavingRef.current = false;
    },
    onSuccess: (saved: any) => {
      const normalized = (saved as any).document || saved;
      setDocMeta((prev) => ({ ...prev, ...normalized }));
      if (typeof normalized.contentRev === 'number') {
        lastKnownContentRevRef.current = normalized.contentRev;
      }
      setConflictState(null);
      setLastSavedTime(new Date().toLocaleTimeString());
      toast.success('Saved — your version now replaces the one that changed elsewhere.');
      queryClient.setQueryData(['document', id], normalized);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => {
      const { message } = parseApiError(e);
      toast.error(`Force-save failed: ${message}`);
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: async () => {
      if (!docMeta.documentId) throw new Error('No document loaded');
      // Change published document back to draft status for editing
      return documentApi.update(docMeta.documentId, { status: 'draft' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Document is now editable as draft');
      window.location.reload();
    },
    onError: (e: Error) => toast.error(`Failed to create draft: ${e.message}`),
  });

  // The browser's back-forward cache (bfcache) can revive this exact page instance - React
  // state, the 60s autosave interval below, all of it - completely frozen from whenever the
  // user last navigated away, with no re-fetch and no re-render in between. Navigate away,
  // let someone else (or another tab) change the document, then hit Back: the revived page
  // has no idea anything changed, and its still-running autosave timer will silently overwrite
  // those changes with what was in memory before it was frozen, on its very next 60s tick.
  // `pageshow`'s `persisted` flag is exactly the signal a bfcache restore happened (a normal
  // navigation never sets it) - force a real reload so this page starts from a genuine fresh
  // fetch instead of resuming on a stale snapshot.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Autosave every 60s for existing drafts
  useEffect(() => {
    if (autoSaveRef.current) {
      window.clearInterval(autoSaveRef.current);
      autoSaveRef.current = null;
    }

    if (isPublished) return;
    if (!docMeta.documentId) return;

    autoSaveRef.current = window.setInterval(() => {
      if (isSavingRef.current) return; // a save (or publish's own pre-save) is already in flight
      saveDraftMutation.mutate();
    }, 60000);

    return () => {
      if (autoSaveRef.current) {
        window.clearInterval(autoSaveRef.current);
        autoSaveRef.current = null;
      }
    };
  }, [docMeta.documentId, isPublished]);

  // Auto-save when register is changed in Properties tab
  useEffect(() => {
    if (!docMeta.documentId || isPublished) return;
    
    const timer = window.setTimeout(() => {
      if (docMeta.registerId && docMeta.registerId !== loadedDoc?.registerId && !isSavingRef.current) {
        console.log('Auto-saving document with new registerId:', docMeta.registerId);
        saveDraftMutation.mutate();
      }
    }, 1000); // Wait 1s before auto-saving after register change

    return () => window.clearTimeout(timer);
  }, [docMeta.registerId, docMeta.documentId, isPublished, loadedDoc?.registerId]);


  const updateSection = useCallback((sectionId: string, newHtml: string) => {
    setSections((prev) =>
      prev ? prev.map((s) => (s.id === sectionId ? { ...s, html: newHtml } : s)) : prev
    );
  }, []);

  const updateSectionHeading = useCallback((sectionId: string, newHeading: string) => {
    setSections((prev) =>
      prev ? prev.map((s) => (s.id === sectionId ? { ...s, heading: newHeading } : s)) : prev
    );
  }, []);

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  // Scrolls to a pending change's own inline preview block, rendered after every section below
  // (that's genuinely where accepting it will land - see the block's own comment). Switches to
  // the Content tab first since the block only exists there; the timeout gives React one paint
  // to mount it before scrollIntoView runs, needed the first time the Properties tab was active.
  const jumpToChange = (changeId: string) => {
    setActiveTab('editor');
    window.setTimeout(() => {
      const el = document.getElementById(`pending-change-${changeId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.remove('np-pending-flash');
      void (el as HTMLElement).offsetWidth; // restart the CSS animation on repeat clicks
      el.classList.add('np-pending-flash');
    }, 60);
  };

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const versionLabel = docMeta.majorVersion !== undefined
    ? `v${docMeta.majorVersion}.${docMeta.minorVersion || 0}`
    : 'v0.0';

  const statusTone = docMeta.status === 'published'
    ? 'success'
    : docMeta.status === 'archived'
      ? 'warning'
      : 'neutral';

  // Same shared htmlToPdf utility ProcessingActivityDetail.tsx/ManagementReviewDetail.tsx use,
  // in the same style - the previous button here was its own one-off html2canvas+jsPDF
  // implementation, only ever shown for a draft, which meant a PUBLISHED document (exactly the
  // state someone most wants a PDF of) had no export option at all. Builds a fresh, self-
  // contained HTML string (title + every section in order) rather than screenshotting the live
  // page, so it works identically whether the document is being edited or read-only.
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const { htmlToPdf } = await import('../utils/pdfExport');
      const cs = (s: string) =>
        s ? String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
      const sectionsHtml = (sections || [])
        .map((s) => `
          <div style="margin-bottom:24px;">
            <h2 style="font-size:16px;font-weight:700;color:#111827;border-bottom:2px solid #4f46e5;padding-bottom:6px;margin:0 0 12px 0;">${cs(s.heading)}</h2>
            <div>${!s.html || !s.html.replace(/<[^>]*>/g, '').trim() ? '<p style="color:#9ca3af;font-style:italic;">No content</p>' : s.html}</div>
          </div>`)
        .join('');
      const html = `
<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:860px;padding:40px;">
  <style>
    table { width:100%; border-collapse:collapse; table-layout:auto; margin:12px 0; }
    td, th { border:1px solid #e5e7eb; padding:6px 10px; font-size:12px; vertical-align:top; text-align:left; }
    th { background:#f9fafb; font-weight:700; }
    p { font-size:13px; line-height:1.7; margin:0 0 8px 0; }
    ul, ol { font-size:13px; line-height:1.7; }
  </style>
  <div style="padding:24px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:28px;">
    <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">${cs(docMeta.documentType || 'Document')}</div>
    <h1 style="font-size:22px;font-weight:800;color:#111827;margin:0 0 10px 0;">${cs(docMeta.title || 'Untitled Document')}</h1>
    <div style="font-size:12px;color:#6b7280;">${cs(docMeta.docNumber || docMeta.documentId || '')} · ${cs(versionLabel)} · ${cs(docMeta.status || 'draft')}</div>
  </div>
  ${sectionsHtml}
  <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">
    Generated ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
  </div>
</div>`;
      await htmlToPdf(html, `${(docMeta.title || 'document').replace(/[^\w\s-]/g, '')}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      swal.error('Export failed', 'Could not generate the PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const backUrl = docMeta.registerId
    ? `/documents?registerId=${encodeURIComponent(docMeta.registerId)}`
    : (registerIdFromUrl ? `/documents?registerId=${encodeURIComponent(registerIdFromUrl)}` : '/documents');

  // A document created outside the register flow (e.g. a per-use-case DPIA, entity-linked
  // rather than register-parented by design) has no register at all - publishing needs one
  // picked or created right here rather than a dead-end error with no recovery path.
  const needsRegister = !docMeta.registerId && !registerIdFromUrl && !pendingRegisterId;

  // One unresolved change's inline preview - rendered wherever pendingBySectionIdx placed it,
  // amber tint/accent (not red - np-tone-danger already means "blocked/wrong" elsewhere on this
  // same page). Accepting/rejecting still reloads the page (sections state is a one-time parse
  // of `content`, same reasoning the mutations above already document), so there's no separate
  // "resolved" visual state to build here: a reload either removes this block (rejected) or the
  // document comes back with the content merged in as plain text (accepted).
  const renderPendingBlock = (c: PendingChange) => (
    <div key={c.id} id={`pending-change-${c.id}`} className="np-pending-block">
      <div className="np-pending-block-tag">
        <i className="ri-route-line"></i> Proposed from{' '}
        <a href={`/ai-use-cases/${encodeURIComponent(c.useCaseId)}`} target="_blank" rel="noreferrer">{c.useCaseId}</a>
        {' '}· awaiting review
      </div>
      {resolvingChangeId === c.id ? (
        <>
          <textarea
            className="np-textarea"
            rows={4}
            value={editHtmlDraft}
            onChange={(e) => setEditHtmlDraft(e.target.value)}
          />
          <div className="np-pending-block-actions">
            <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setResolvingChangeId(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="np-btn np-btn-primary np-btn-sm"
              disabled={acceptChangeMutation.isPending}
              onClick={() => acceptChangeMutation.mutate({ changeId: c.id, editedHtml: editHtmlDraft })}
            >
              Accept edit
            </button>
          </div>
        </>
      ) : (
        <>
          <div dangerouslySetInnerHTML={{ __html: c.proposedHtml }} />
          <div className="np-pending-block-actions">
            <button
              type="button"
              className="np-btn np-btn-success np-btn-sm"
              disabled={acceptChangeMutation.isPending}
              onClick={() => acceptChangeMutation.mutate({ changeId: c.id })}
            >
              <i className="ri-check-line"></i>Accept
            </button>
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => { setResolvingChangeId(c.id); setEditHtmlDraft(c.proposedHtml); }}
            >
              <i className="ri-edit-line"></i>Edit first
            </button>
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              disabled={rejectChangeMutation.isPending}
              onClick={async () => {
                const ok = await swal.confirm('Reject this proposed change?', 'It will not be added to the document. This cannot be undone.');
                if (ok) rejectChangeMutation.mutate(c.id);
              }}
            >
              <i className="ri-close-line"></i>Reject
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* ── Top Bar ─────────────────────────────────────────── */}
      <PageHeader
        icon="ri-file-text-line"
        title={docMeta.title || (id ? 'Document Editor' : 'New Document')}
        colour="primary"
        actions={
          <>
            <Link to={backUrl} className="np-btn np-btn-ghost np-btn-sm">
              <i className="ri-arrow-left-line"></i>Back
            </Link>
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={handleExportPdf}
              disabled={exportingPdf}
            >
              {exportingPdf
                ? <><span className="spinner-border spinner-border-sm"></span>Exporting…</>
                : <><i className="ri-file-pdf-line"></i>Export PDF</>
              }
            </button>
            {!isPublished && isLatestVersion && (
              <button className="np-btn np-btn-success np-btn-sm" onClick={() => saveDraftMutation.mutate()} disabled={saveDraftMutation.isPending}>
                {saveDraftMutation.isPending
                  ? <><span className="spinner-border spinner-border-sm"></span>Saving…</>
                  : <><i className="ri-save-line"></i>Save</>
                }
              </button>
            )}
            {!isPublished && isLatestVersion && (
              <button
                className="np-btn np-btn-primary np-btn-sm"
                onClick={() => {
                  // Pre-fill a real changelog line from whichever AI use cases have an
                  // ACCEPTED pending change on this draft since the last publish, matching this
                  // document's own "Description of Change" convention - the reviewer can still
                  // edit or replace it before publishing. A rejected change contributes nothing.
                  if (acceptedChanges.length > 0 && !changeReasonInput.trim()) {
                    const ids = acceptedChanges.map((c) => c.useCaseId);
                    setChangeReasonInput(
                      `Incorporates approved AI use case${ids.length > 1 ? 's' : ''}: ${ids.join(', ')}`
                    );
                  }
                  setShowPublishModal(true);
                }}
                disabled={publishMutation.isPending || unresolvedChanges.length > 0}
                title={unresolvedChanges.length > 0
                  ? `${unresolvedChanges.length} pending change${unresolvedChanges.length !== 1 ? 's' : ''} must be accepted or rejected first - see below.`
                  : undefined}
              >
                <i className="ri-checkbox-circle-line"></i>Publish
              </button>
            )}
            {isPublished && isLatestVersion && (
              <button className="np-btn np-btn-primary np-btn-sm" onClick={() => createDraftMutation.mutate()} disabled={createDraftMutation.isPending}>
                <i className="ri-file-edit-line"></i>
                {createDraftMutation.isPending ? 'Creating…' : 'Edit (New Draft)'}
              </button>
            )}
          </>
        }
      />

      {/* Status/version/type chips */}
      <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
        <span className={`np-chip np-tone-${statusTone} text-capitalize`}>
          {docMeta.status || 'draft'}
        </span>
        <span className="np-chip np-tone-info np-tabular">{versionLabel}</span>
        {docMeta.documentType && (
          <span className="np-chip np-tone-primary">{docMeta.documentType}</span>
        )}
      </div>

      {/* ── Status Alerts ───────────────────────────────────── */}
      {conflictState && (
        <div className="np-banner np-tone-danger mb-3 align-items-center">
          <i className="ri-error-warning-line"></i>
          <small>
            {conflictState.message} Autosave is paused until you choose one of the options below.
          </small>
          <div className="d-flex gap-2 ms-auto flex-shrink-0">
            <button
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => forceSaveMutation.mutate()}
              disabled={forceSaveMutation.isPending}
            >
              {forceSaveMutation.isPending ? 'Saving…' : 'Save my version anyway'}
            </button>
            <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => window.location.reload()}>
              Reload to see their version
            </button>
          </div>
        </div>
      )}
      {lastSavedTime && !isPublished && (
        <div className="np-banner np-tone-success mb-3 align-items-center">
          <i className="ri-checkbox-circle-line"></i>
          <small>Saved at {lastSavedTime}</small>
          <button className="btn-close ms-auto" style={{ fontSize: '0.6rem' }} onClick={() => setLastSavedTime('')}></button>
        </div>
      )}
      {isPublished && docMeta.publishedAt && (
        <div className="np-banner np-tone-info mb-3">
          <i className="ri-lock-line"></i>
          <small>Published {new Date(docMeta.publishedAt).toLocaleString()} — read-only</small>
        </div>
      )}
      {!isLatestVersion && (
        <div className="np-banner np-tone-warning mb-3">
          <i className="ri-error-warning-line"></i>
          <small>
            You are viewing an older version — read-only.{' '}
            {versions.length > 0 && (
              <a href={`/documents/${encodeURIComponent(versions[0]?.documentId || versions[0]?.id)}`} className="fw-semibold">
                View latest
              </a>
            )}
          </small>
        </div>
      )}

      {/* Pending Changes - AI-drafted content proposed by an approval, never a silent edit to
          `content`. Each row must be individually accepted (optionally edited first) or
          rejected before this document can be published - see the disabled Publish button
          above when any remain unresolved.

          This list is deliberately just a table of contents now, not the only place a change
          is visible: every unresolved entry ALSO renders inline, in the body, at the exact spot
          it will land (the end of the document - see the block below sections.map()) - amber
          tint + left accent, this app's own existing "pending" vocabulary, not raw red text
          (np-tone-danger already means "blocked/wrong" elsewhere on this same page, e.g. the
          special-category-data banner in ProcessingActivityDetail.tsx's own sibling register).
          Clicking a row jumps straight to that block instead of leaving the reviewer to guess
          where an accepted change will actually go. */}
      {pendingChanges.length > 0 && isLatestVersion && (
        <div className="np-card mb-3">
          <div className="np-card-head">
            <h2><i className="ri-git-pull-request-line"></i> Pending Changes</h2>
            {unresolvedChanges.length > 0 && (
              <span className="np-chip np-tone-warning">{unresolvedChanges.length} awaiting review</span>
            )}
          </div>
          <div className="np-card-body p-0">
            {pendingChanges.map((c) => {
              const isPending = c.status === 'pending';
              return (
                <div key={c.id} className="np-pending-toc-row">
                  <button
                    type="button"
                    className="np-pending-toc-main"
                    onClick={() => isPending ? jumpToChange(c.id) : undefined}
                    disabled={!isPending}
                    title={isPending ? 'Jump to this change in the document below' : undefined}
                  >
                    <span className={`np-pending-toc-src ${c.status === 'accepted' ? 'np-tone-success' : c.status === 'rejected' ? 'np-tone-neutral' : ''}`}>
                      <i className={c.status === 'accepted' ? 'ri-check-line' : c.status === 'rejected' ? 'ri-close-line' : 'ri-route-line'}></i>
                      {c.useCaseId}
                    </span>
                    <span
                      className={`np-pending-toc-excerpt${c.status === 'rejected' ? ' rejected' : ''}`}
                      dangerouslySetInnerHTML={{ __html: c.finalHtml || c.proposedHtml }}
                    />
                    {isPending && (
                      <span className="np-pending-toc-jump"><i className="ri-arrow-down-line"></i> Jump to preview</span>
                    )}
                  </button>
                  {isPending && (
                    resolvingChangeId === c.id ? (
                      <div className="d-flex align-items-start gap-2 px-3 pb-3">
                        <textarea
                          className="np-textarea flex-grow-1"
                          rows={4}
                          value={editHtmlDraft}
                          onChange={(e) => setEditHtmlDraft(e.target.value)}
                        />
                        <div className="d-flex flex-column gap-1 flex-shrink-0">
                          <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setResolvingChangeId(null)}>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="np-btn np-btn-primary np-btn-sm"
                            disabled={acceptChangeMutation.isPending}
                            onClick={() => acceptChangeMutation.mutate({ changeId: c.id, editedHtml: editHtmlDraft })}
                          >
                            Accept edit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex gap-1 justify-content-end px-3 pb-2">
                        <button
                          type="button"
                          className="np-icon-btn"
                          title="Edit before accepting"
                          onClick={() => { setResolvingChangeId(c.id); setEditHtmlDraft(c.proposedHtml); }}
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          type="button"
                          className="np-icon-btn np-icon-btn-success"
                          title="Accept as-is"
                          disabled={acceptChangeMutation.isPending}
                          onClick={() => acceptChangeMutation.mutate({ changeId: c.id })}
                        >
                          <i className="ri-check-line"></i>
                        </button>
                        <button
                          type="button"
                          className="np-icon-btn np-icon-btn-danger"
                          title="Reject"
                          disabled={rejectChangeMutation.isPending}
                          onClick={async () => {
                            const ok = await swal.confirm('Reject this proposed change?', 'It will not be added to the document. This cannot be undone.');
                            if (ok) rejectChangeMutation.mutate(c.id);
                          }}
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tab Navigation ──────────────────────────────────── */}
      <div className="np-tabs">
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'editor' ? ' active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          <i className="ri-edit-2-line"></i>Content
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'meta' ? ' active' : ''}`}
          onClick={() => setActiveTab('meta')}
        >
          <i className="ri-settings-3-line"></i>Properties
        </button>
      </div>

      {/* ── Editor Tab ──────────────────────────────────────── */}
      {activeTab === 'editor' && (
        <div className="row g-4">
          {/* Main content area */}
          <div className="col-12 col-xl-9">
            {sections === null ? (
              <div className="np-card">
                <div className="np-card-body text-center py-5 np-sub">
                  <i className="ri-loader-4-line ri-spin me-1"></i>Loading document…
                </div>
              </div>
            ) : (
              /* ── Section-based document editor ── */
              <div data-pdf-export="true">
                {hasLegacyDeltaContent && (
                  <div className="np-banner np-tone-warning mb-3">
                    <i className="ri-error-warning-line"></i>
                    <small>
                      This document's saved content is in an old, unsupported format and could
                      not be loaded into the editor. Start writing below to replace it.
                    </small>
                  </div>
                )}

                {/* Editable document title */}
                <div className="mb-4 pb-3" style={{ borderBottom: '2px solid var(--bs-border-color-translucent)' }}>
                  {isPublished || !isLatestVersion ? (
                    <h2 className="fw-bold mb-1" style={{ fontSize: '1.7rem' }}>{docMeta.title || 'Untitled Document'}</h2>
                  ) : (
                    <input
                      className="fw-bold w-100 border-0 p-0 bg-transparent"
                      style={{ fontSize: '1.7rem', outline: 'none', boxShadow: 'none', color: 'var(--bs-heading-color, inherit)', lineHeight: 1.3 }}
                      value={docMeta.title || ''}
                      onChange={(e) => setDocMeta((p) => ({ ...p, title: e.target.value }))}
                      placeholder="Document title…"
                    />
                  )}
                  {docMeta.documentType && (
                    <span className="np-chip np-tone-primary mt-1">{docMeta.documentType}</span>
                  )}
                </div>

                {sections.length === 0 && (
                  <div className="np-banner np-tone-neutral">
                    <i className="ri-information-line"></i>No sections found.
                  </div>
                )}

                {!isPublished && isLatestVersion && sections.length > 0 && (
                  <p className="np-sub mb-3" style={{ fontSize: '0.8rem' }}>
                    <i className="ri-cursor-fill me-1"></i>Click a section heading to rename it.
                  </p>
                )}

                {sections.map((section, idx) => {
                  const isCollapsed = collapsedSections.has(section.id);
                  return (
                    <React.Fragment key={section.id}>
                    <div className="mb-3" style={{
                      background: 'var(--np-surface)',
                      borderRadius: '0.375rem',
                      border: '1px solid var(--bs-border-color-translucent)',
                      borderLeft: '4px solid var(--bs-primary)',
                      // No overflow:hidden here - it would make this card the sticky positioning
                      // containing block for PolicyEditor's own sticky toolbar nested inside it,
                      // the exact bug already fixed once for .np-policy-editor itself (see
                      // policy-editor.css's .np-policy-editor-sticky-toolbar comment). Since this
                      // card never scrolls internally (the page does), that would make the
                      // toolbar silently stop sticking the moment a section is long enough to
                      // scroll. Instead, each child rounds only the corners it actually owns.
                    }}>
                      {/* Section heading row - rounds all 4 corners when collapsed (it's the
                          whole card then), otherwise just the top 2 (PolicyEditor's own
                          .np-policy-editor-content-wrap rounds the bottom 2 when expanded). */}
                      <div className="d-flex align-items-center gap-2 px-4 py-3" style={{
                        borderBottom: isCollapsed ? 'none' : '1px solid var(--bs-border-color-translucent)',
                        background: 'rgba(var(--bs-primary-rgb), 0.03)',
                        borderRadius: isCollapsed ? '0.375rem' : '0.375rem 0.375rem 0 0',
                      }}>
                        <span className="text-primary fw-bold flex-shrink-0" style={{ fontSize: '0.85rem', minWidth: 20 }}>{idx + 1}.</span>
                        {isPublished || !isLatestVersion ? (
                          <h5 className="mb-0 fw-semibold flex-grow-1" style={{ fontSize: '1rem' }}>{section.heading}</h5>
                        ) : (
                          <input
                            className="flex-grow-1 border-0 fw-semibold p-0 bg-transparent"
                            style={{ fontSize: '1rem', outline: 'none', minWidth: 0, color: 'var(--bs-body-color)' }}
                            value={section.heading}
                            onChange={(e) => updateSectionHeading(section.id, e.target.value)}
                            placeholder="Section heading…"
                          />
                        )}
                        {!isPublished && isLatestVersion && (
                          <>
                            {idx > 0 && (
                              <button
                                type="button"
                                className="np-icon-btn flex-shrink-0"
                                onClick={() => setSections((prev) => {
                                  if (!prev) return prev;
                                  const newSections = [...prev];
                                  [newSections[idx - 1], newSections[idx]] = [newSections[idx], newSections[idx - 1]];
                                  return newSections;
                                })}
                                title="Move up"
                              >
                                <i className="ri-arrow-up-s-line" style={{ fontSize: '1.1rem' }}></i>
                              </button>
                            )}
                            {idx < (sections?.length || 0) - 1 && (
                              <button
                                type="button"
                                className="np-icon-btn flex-shrink-0"
                                onClick={() => setSections((prev) => {
                                  if (!prev) return prev;
                                  const newSections = [...prev];
                                  [newSections[idx], newSections[idx + 1]] = [newSections[idx + 1], newSections[idx]];
                                  return newSections;
                                })}
                                title="Move down"
                              >
                                <i className="ri-arrow-down-s-line" style={{ fontSize: '1.1rem' }}></i>
                              </button>
                            )}
                          </>
                        )}
                        <button
                          type="button"
                          className="np-icon-btn flex-shrink-0"
                          onClick={() => toggleSection(section.id)}
                          title={isCollapsed ? 'Expand section' : 'Collapse section'}
                        >
                          <i className={`ri-arrow-${isCollapsed ? 'down' : 'up'}-s-line`} style={{ fontSize: '1.1rem' }}></i>
                        </button>
                        {!isPublished && isLatestVersion && (
                          <button
                            type="button"
                            className="np-icon-btn np-icon-btn-danger flex-shrink-0"
                            onClick={async () => {
                              const ok = await swal.confirm(`Delete the "${section.heading}" section? This cannot be undone.`);
                              if (!ok) return;
                              setSections((prev) => prev ? prev.filter((s) => s.id !== section.id) : prev);
                            }}
                            title="Delete section"
                          >
                            <i className="ri-delete-bin-line" style={{ fontSize: '1rem' }}></i>
                          </button>
                        )}
                      </div>

                      {/* Content editor */}
                      {!isCollapsed && (
                        <div style={{ padding: '0.25rem 1.25rem 0.75rem' }}>
                          <PolicyEditor
                            value={section.html}
                            onChange={(html) => {
                              if (isPublished || !isLatestVersion) return;
                              updateSection(section.id, html);
                            }}
                            readOnly={isPublished || !isLatestVersion}
                            placeholder="Write section content here…"
                            minHeight={100}
                            documentId={docMeta.documentId}
                          />
                        </div>
                      )}
                    </div>
                    {isLatestVersion && (pendingBySectionIdx.get(idx) || []).map(renderPendingBlock)}
                    </React.Fragment>
                  );
                })}

                {!isPublished && isLatestVersion && (
                  <div className="d-flex gap-2 mt-3">
                    <button
                      className="np-btn np-btn-ghost np-btn-sm"
                      onClick={() => setSections((prev) => [
                        ...(prev || []),
                        { id: `section-${Date.now()}`, heading: 'New Section', html: '<p></p>' }
                      ])}
                    >
                      <i className="ri-add-line"></i>Add section
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <div className="col-12 col-xl-3">
            {/* Version history */}
            <div className="np-card mb-3">
              <div className="np-card-head">
                <h2 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <i className="ri-history-line"></i> Version History
                </h2>
              </div>
              <div className="np-card-body py-2">
                {versions.length === 0 ? (
                  <p className="np-sub mb-0"><i className="ri-history-line me-1"></i>No published versions yet</p>
                ) : (
                  <>
                    <select
                      className="np-select mb-2"
                      value={id || ''}
                      onChange={(e) => {
                        const vId = e.target.value;
                        if (vId) navigate(`/documents/${encodeURIComponent(vId)}`);
                      }}
                    >
                      {versions.map((v: any, index: number) => {
                        const vId = v.documentId || v.versionId || v.id;
                        const vLabel = v.versionLabel || `${v.majorVersion || 0}.${v.minorVersion || 0}`;
                        const ts = v.publishedAt || v.createdAt;
                        const dateStr = ts ? new Date(ts * 1000).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
                        return (
                          <option key={String(vId)} value={vId}>
                            v{vLabel}{index === 0 ? ' (Latest)' : ''}{dateStr ? ` · ${dateStr}` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <button type="button" className="np-btn np-btn-ghost np-btn-sm w-100" onClick={() => setShowChangeLogModal(true)}>
                      <i className="ri-file-list-3-line"></i>View change log
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* RACI */}
            {docMeta.documentId && (
              <div className="np-card">
                <div className="np-card-head">
                  <h2 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <i className="ri-team-line"></i> RACI
                  </h2>
                </div>
                <div className="np-card-body py-2">
                  <RaciAssignments artefactId={docMeta.documentId} artefactType="document" compact={true} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Properties Tab ──────────────────────────────────── */}
      {activeTab === 'meta' && (
        <>
        <div className="row g-4">
          <div className="col-12 col-md-6 col-xl-4">
            <div className="np-card h-100">
              <div className="np-card-head">
                <h2 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <i className="ri-file-info-line"></i> Basic Info
                </h2>
              </div>
              <div className="np-card-body">
                <div className="np-field">
                  <label>Document ID</label>
                  <input className="np-input" value={docMeta.docNumber || docMeta.documentId || 'Auto-generated after save'} disabled />
                </div>
                <div className="np-field">
                  <label>Document Register <span className="text-danger">*</span></label>
                  <select
                    className="np-select"
                    value={docMeta.registerId || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, registerId: e.target.value || undefined }))}
                  >
                    <option value="">Select register...</option>
                    {registers.map((r: any) => (
                      <option key={r.registerId} value={r.registerId}>
                        {r.name || r.registerName || r.registerId}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="np-field">
                  <label>Title <span className="text-danger">*</span></label>
                  <input
                    className="np-input"
                    placeholder="Enter document title"
                    value={docMeta.title || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, title: e.target.value }))}
                    disabled={isPublished}
                  />
                </div>
                <div className="np-field">
                  <label>Document Type <span className="text-danger">*</span></label>
                  <select
                    className="np-select"
                    value={docMeta.documentType || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, documentType: e.target.value }))}
                    disabled={isPublished}
                  >
                    <option value="">Select type…</option>
                    {/* The AI use case approval flow writes documentType 'DPIA' directly
                        (lambda/prod-nimbus-documents/lambda_function.py's
                        _internal_create_dpia_draft) - without this option, opening one of
                        those documents here rendered the picker as if nothing were selected,
                        even though a real, valid type was already stored. */}
                    <option value="DPIA">DPIA</option>
                    <option value="Policy">Policy</option>
                    <option value="Procedure">Procedure</option>
                    <option value="Process">Process</option>
                    <option value="Standard">Standard</option>
                    <option value="Guideline">Guideline</option>
                    <option value="Plan">Plan</option>
                    <option value="Report">Report</option>
                    <option value="Manual">Manual</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="np-field mb-0">
                  <label>Domain Area</label>
                  <input
                    className="np-input"
                    placeholder="e.g., Information Security"
                    value={docMeta.domainArea || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, domainArea: e.target.value }))}
                    disabled={isPublished}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-4">
            <div className="np-card h-100">
              <div className="np-card-head">
                <h2 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <i className="ri-user-settings-line"></i> Ownership & Classification
                </h2>
              </div>
              <div className="np-card-body">
                <div className="np-field">
                  <label>Author</label>
                  <Select
                    options={people.map((person: any) => ({ value: person.personId, label: person.personName }))}
                    value={(() => {
                      const opt = people.find((person) => person.personId === docMeta.author);
                      return opt ? { value: opt.personId, label: opt.personName } : null;
                    })()}
                    onChange={(opt) => setDocMeta((p) => ({ ...p, author: opt?.value || undefined }))}
                    isDisabled={isPublished}
                    placeholder="Select person…"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={reactSelectStyles()}
                  />
                </div>
                <div className="np-field">
                  <label>Team</label>
                  <select
                    className="np-select"
                    value={docMeta.departmentId || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, departmentId: e.target.value || undefined }))}
                    disabled={isPublished}
                  >
                    <option value="">Select team…</option>
                    {departments.map((dept) => (
                      <option key={dept.departmentId} value={dept.departmentId}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <div className="np-field mb-0">
                  <label>Confidentiality Level</label>
                  <select
                    className="np-select"
                    value={docMeta.confidentialityLevel || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, confidentialityLevel: e.target.value }))}
                    disabled={isPublished}
                  >
                    <option value="">Select level…</option>
                    <option value="Public">Public</option>
                    <option value="Internal">Internal</option>
                    <option value="Confidential">Confidential</option>
                    <option value="Restricted">Restricted</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-4">
            <div className="np-card h-100">
              <div className="np-card-head">
                <h2 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <i className="ri-calendar-check-line"></i> Review Schedule
                </h2>
              </div>
              <div className="np-card-body">
                <div className="np-field">
                  <label>Review Period</label>
                  <select
                    className="np-select"
                    value={docMeta.reviewPeriod || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, reviewPeriod: e.target.value }))}
                    disabled={isPublished}
                  >
                    <option value="">Select period…</option>
                    <option value="1 month">Monthly</option>
                    <option value="3 months">Quarterly</option>
                    <option value="6 months">Bi-annually</option>
                    <option value="1 year">Annually</option>
                  </select>
                </div>
                <div className="np-field mb-0">
                  <label>Next Review Date</label>
                  <input
                    type="date"
                    className="np-input np-tabular"
                    value={docMeta.nextReviewDate || ''}
                    onChange={(e) => setDocMeta((p) => ({ ...p, nextReviewDate: e.target.value }))}
                    disabled={isPublished}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Linked Artefacts */}
        {docMeta.documentId && (
          <div className="mt-4">
            <LinkedArtefacts
              artefactId={docMeta.documentId}
              artefactType="document"
              artefactRegister="document_register"
              registerId={registerIdFromUrl}
            />
          </div>
        )}
        </>
      )}

      {/* Part 4 - Risk Assessment. Only activates on a DPIA document - the one place this
          template's 11 dimensions stop being permanently unqueryable prose. A new, separate
          rollup field (residualRiskLevel, NOT dpiaVerdict - see constants/privacy.ts) is
          computed here and shown as its own chip. Deliberately placed after both tabs, not
          above them - it reads as "Part 4", so it belongs after Parts 1-3 in the document
          content above it, not before a reader has even seen the document itself (confirmed
          live as a real complaint: it used to render above everything, including the title). */}
      {docMeta.documentType === 'DPIA' && (
        <div className="np-card mb-3">
          <div className="np-card-head">
            <h2><i className="ri-shield-flash-line"></i> Part 4 — Risk Assessment</h2>
            <span className={`np-chip np-tone-${RESIDUAL_RISK_TONE[docMeta.residualRiskLevel || '']}`}>
              {RESIDUAL_RISK_LABELS[docMeta.residualRiskLevel || '']}
            </span>
          </div>
          <div className="np-card-body">
            <p className="np-sub">
              Score each dimension, record the mitigation, and confirm whether the residual risk
              is acceptable. This is a real, structured assessment - not part of the document's
              free-text sections above.
            </p>
            <NimbusTable
              thead={<tr>
                <th style={{ minWidth: '12rem' }}>Dimension</th>
                <th style={{ width: '140px' }}>Risk</th>
                <th style={{ minWidth: '260px' }}>Mitigation</th>
                <th style={{ width: '110px' }}>Accept residual</th>
                <th style={{ width: '90px' }}></th>
              </tr>}
            >
              {DPIA_RISK_DIMENSIONS.map((dim) => (
                <DpiaRiskDimensionRow
                  key={dim}
                  dimension={dim}
                  saved={(docMeta.riskDimensions || []).find((d) => d.dimension === dim)}
                  saving={upsertRiskDimensionMutation.isPending}
                  readOnly={isPublished || !isLatestVersion}
                  onSave={(riskScore, mitigation, residualRiskAcceptable) =>
                    upsertRiskDimensionMutation.mutate({ dimension: dim, riskScore, mitigation, residualRiskAcceptable })
                  }
                />
              ))}
            </NimbusTable>
          </div>
        </div>
      )}

      {/* ── Publish Modal ───────────────────────────────────── */}
      {showPublishModal && (
        <NimbusModal>
          <NimbusModalContent
            title={<><i className="ri-checkbox-circle-line me-2 text-primary"></i>Publish Document</>}
            onClose={() => { setShowPublishModal(false); setChangeReasonInput(''); }}
            footer={
              <button type="button" className="np-btn np-btn-ghost" onClick={() => { setShowPublishModal(false); setChangeReasonInput(''); }}>Cancel</button>
            }
          >
            {needsRegister && (
              <div className="np-banner np-tone-warning mb-3">
                <i className="ri-error-warning-line"></i>
                <div className="flex-grow-1">
                  <strong>This document isn't linked to a register yet.</strong> Pick one or create a new one to publish.
                  <div className="np-field np-mb-0 mt-2">
                    <label>Register</label>
                    <select
                      className="np-select"
                      value={pendingRegisterId}
                      onChange={(e) => setPendingRegisterId(e.target.value)}
                      disabled={showNewRegisterField}
                    >
                      <option value="">Select register...</option>
                      {registers.map((r: any) => (
                        <option key={r.registerId} value={r.registerId}>{r.name || r.registerName || r.registerId}</option>
                      ))}
                    </select>
                  </div>
                  {showNewRegisterField ? (
                    <div className="np-field np-mb-0 mt-2">
                      <label>New register name</label>
                      <div className="d-flex gap-2">
                        <input
                          className="np-input"
                          value={newRegisterName}
                          onChange={(e) => setNewRegisterName(e.target.value)}
                          placeholder="e.g. Data Protection Impact Assessments"
                        />
                        <button
                          type="button"
                          className="np-btn np-btn-primary np-btn-sm"
                          disabled={!newRegisterName.trim() || createRegisterMutation.isPending}
                          onClick={() => createRegisterMutation.mutate(newRegisterName.trim())}
                        >
                          {createRegisterMutation.isPending ? 'Creating…' : 'Create'}
                        </button>
                        <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => { setShowNewRegisterField(false); setNewRegisterName(''); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="np-btn np-btn-ghost np-btn-sm mt-2" onClick={() => setShowNewRegisterField(true)}>
                      <i className="ri-add-line"></i> Create new register
                    </button>
                  )}
                </div>
              </div>
            )}
            <p className="np-sub">Choose the version type to create when publishing:</p>
            <div className="d-grid gap-2 mb-3">
              <button
                className="np-btn np-btn-primary"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0.75rem 1rem' }}
                onClick={() => publishMutation.mutate({ versionType: 'major', changeReason: changeReasonInput })}
                disabled={publishMutation.isPending || !changeReasonInput.trim() || needsRegister}
              >
                <div className="fw-bold">Major Version</div>
                <small className="text-white-50">
                  {docMeta.majorVersion !== undefined
                    ? `${docMeta.majorVersion}.${docMeta.minorVersion || 0} → ${(docMeta.majorVersion || 0) + 1}.0`
                    : '0.0 → 1.0'}
                </small>
              </button>
              <button
                className="np-btn np-btn-ghost"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0.75rem 1rem' }}
                onClick={() => publishMutation.mutate({ versionType: 'minor', changeReason: changeReasonInput })}
                disabled={publishMutation.isPending || !changeReasonInput.trim() || needsRegister}
              >
                <div className="fw-bold">Minor Version</div>
                <small className="np-sub">
                  {docMeta.majorVersion !== undefined
                    ? `${docMeta.majorVersion}.${docMeta.minorVersion || 0} → ${docMeta.majorVersion}.${(docMeta.minorVersion || 0) + 1}`
                    : '0.0 → 0.1'}
                </small>
              </button>
            </div>
            <div className="np-field np-mb-0">
              <label>Reason for this change <span className="text-danger">*</span></label>
              <textarea
                className="np-textarea"
                rows={2}
                value={changeReasonInput}
                onChange={(e) => setChangeReasonInput(e.target.value)}
                placeholder="What changed and why - this becomes part of the document's permanent change log."
              />
            </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* ── Change Log Modal ────────────────────────────────── */}
      {showChangeLogModal && (
        <NimbusModal size="lg" scrollable>
          <NimbusModalContent
            title={<><i className="ri-file-list-3-line me-2 text-primary"></i>Change Log</>}
            onClose={() => setShowChangeLogModal(false)}
            footer={
              <button type="button" className="np-btn np-btn-ghost" onClick={() => setShowChangeLogModal(false)}>Close</button>
            }
          >
            <NimbusTable
              thead={<tr>
                <th style={{ width: '90px' }}>Version</th>
                <th style={{ width: '130px' }}>Date</th>
                <th style={{ width: '160px' }}>Changed by</th>
                <th>Reason</th>
                <th style={{ width: '160px' }}>Contributed by</th>
              </tr>}
              isEmpty={versions.length === 0}
              emptyState={<p className="np-sub mb-0 text-center py-3"><i className="ri-history-line me-1"></i>No published versions yet.</p>}
            >
              {versions.map((v: any, index: number) => {
                const vLabel = v.versionLabel || `${v.majorVersion || 0}.${v.minorVersion || 0}`;
                const ts = v.publishedAt || v.createdAt;
                const dateStr = ts ? new Date(ts * 1000).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
                // The snapshot is a plain copy of the live document at the moment it was
                // published (see publish_document's current_snapshot), pendingChanges included -
                // real per-contribution links, not the free-text changeReason's bare id string.
                const contributed: PendingChange[] = (v.pendingChanges || []).filter((c: PendingChange) => c.status === 'accepted');
                return (
                  <tr key={String(v.documentId || v.versionId || v.id)}>
                    <td className="np-tabular">v{vLabel}{index === 0 ? <span className="np-chip np-tone-success ms-2">Latest</span> : null}</td>
                    <td className="np-tabular">{dateStr}</td>
                    <td>{v.changedByName || '—'}</td>
                    <td>{v.changeReason || <span style={{ color: 'var(--bs-secondary-color)' }}>No reason recorded</span>}</td>
                    <td>
                      {contributed.length > 0 ? (
                        <div className="d-flex flex-column gap-1">
                          {contributed.map((c) => (
                            <a key={c.id} href={`/ai-use-cases/${encodeURIComponent(c.useCaseId)}`} target="_blank" rel="noreferrer" className="np-tabular">
                              {c.useCaseId}
                            </a>
                          ))}
                        </div>
                      ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </NimbusTable>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
